import { Box, Flex, Icon, Tag } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { LibraryObjectType, ALL_LIBRARY_OBJECT_FILTERS } from '@fullstory/routes';
import React, { useMemo } from 'react';
import { OBJECT_TYPE_TO_LABEL, OBJECT_TYPE_TO_ICON_NAME } from '../../constants/libraryObjects';
import { objectTypeAnalyticsString } from '../../utils';

const TAG_TEST_ID = 'object-type-filter-pill';
const dataFsPropertiesSchema =
  '{"data-object-type-filter-pill":"str","data-object-type-filter-pill-location":"str"}';

interface ObjectTagFiltersProps {
  /**
   * An ordered set of library object types.
   */
  objectTypes: LibraryObjectType[];
  /**
   * The library object type that is selected or active.
   *
   * null means All, or no filters selected.
   */
  selectedObjectType: LibraryObjectType | null;
  /**
   * null means All, or no filters selected.
   */
  onSelectedObjectTypeChange: (objectType: LibraryObjectType | null) => void;
  /**
   * Padding supplied to the container Box.
   */
  padding?: React.ComponentProps<typeof Box>['padding'];
  /**
   * For analytics purposes.
   */
  location: string;

  showAllFilter?: boolean;
}

export const ObjectTagFilters: React.FC<ObjectTagFiltersProps> = ({
  objectTypes,
  selectedObjectType,
  onSelectedObjectTypeChange,
  padding,
  location,
  showAllFilter = true,
}) => {
  // keep the sort order consistent across usages
  const sortedObjectTypes = useMemo(
    () => ALL_LIBRARY_OBJECT_FILTERS.filter(o => objectTypes.includes(o)),
    [objectTypes],
  );

  return (
    <Box
      data-component="ObjectTagFilters"
      display="flex"
      gap={8}
      padding={padding}
      maxWidth={1}
      overflowX="auto"
    >
      {showAllFilter && (
        <Tag
          active={!selectedObjectType}
          onClick={() => onSelectedObjectTypeChange(null)}
          testId={TAG_TEST_ID}
          data-object-type-filter-pill="All"
          data-object-type-filter-pill-location={location}
          data-fs-properties-schema={dataFsPropertiesSchema}
        >
          {intl.formatMessage({ defaultMessage: 'All', id: 'zQvVDJ' })}
        </Tag>
      )}
      {sortedObjectTypes.map(objectType => {
        const label = OBJECT_TYPE_TO_LABEL[objectType];
        const iconName = OBJECT_TYPE_TO_ICON_NAME[objectType];
        const active = selectedObjectType === objectType;
        const onClick = () => {
          onSelectedObjectTypeChange(active ? null : objectType);
        };

        return (
          <Tag
            key={objectType}
            active={active}
            onClick={onClick}
            testId={TAG_TEST_ID}
            data-object-type-filter-pill={objectTypeAnalyticsString[objectType] ?? ''}
            data-object-type-filter-pill-location={location}
            data-fs-properties-schema={dataFsPropertiesSchema}
          >
            <Flex gap={8}>
              <Icon name={iconName} />
              {label}
            </Flex>
          </Tag>
        );
      })}
    </Box>
  );
};
